import React, { useEffect, useContext, useState } from "react";
import BqnContext from "common/BqnContext";
import ActionsContext from "common/ActionsContext";
import {
  atBqnTargetOrDetail,
  bqnIdTarget,
  parseHostname,
  parseHostnames,
} from "common/api";
import { forgetNodeName } from "common/hostnames";
import styled from "styled-components";
import Modal from "common/Modal";
import TextInput from "common/TextInput";
import Table from "common/graphs/Table/Table";
import ArrangeInColumns from "common/layouts/ArrangeInColumns";

const TableContainer = styled.div`
  position: relative;
  padding: 0;
  td div {
    cursor: pointer;
    display: flex;
    align-items: center;

    i {
      font-size: 20px;
      margin-left: 10px;
    }
  }
  table.MuiTable-root {
    min-width: auto !important;
    max-width: auto !important;
  }
  @media (max-width: 1300px) {
    #table-search {
      width: 120px;
    }
  }
  @media (max-width: 875px) {
    #table-search {
      width: auto;
    }
  }
  & > div > div {
    padding-bottom: 0;
  }
`;

const Wrapper = styled.div`
  background: var(--Background-Block);
  position: relative;
  border-radius: 5px;
  padding: 0;
  .list-hosts {
    overflow: visible;
  }
`;

const ButtonWrapper = styled.div`
  margin-bottom: -40px;
`;

function HostnameModal({ onAppliedChange }) {
  const {
    clusterStatus: { mode },
    loadHostnames,
  } = useContext(BqnContext);
  const [open, setOpen] = useState(false);
  const [bqn, setBqn] = useState("");
  const [hostname, setHostname] = useState("");
  const [errorFooter, setErrorFooter] = useState("");
  const actions = useContext(ActionsContext);
  useEffect(() => {
    return actions.recv("edit-hostname", function (params) {
      const { bqn, hostname } = params;
      setErrorFooter("");
      setBqn(bqn);
      setHostname(hostname);
      setOpen(true);
    });
  }, []);
  if (open === false) {
    return null;
  }

  const doApply = () => {
    const cmd = `system hostname ${hostname}${
      mode === "cluster" ? ` bqn-id ${bqn}` : ""
    }`;
    return ifCl
      .run(cmd)
      .then((response) => {
        if (response.length > 0) {
          throw new Error(response);
        } else {
          // The name kept for the BQN is the one it had: it is dropped and
          // the BQN is asked for its name again, so that the selectors and
          // the cluster table report the name it was given.
          forgetNodeName(bqn);
          loadHostnames().catch(console.error);
          onAppliedChange();
          setOpen(false);
        }
      })
      .catch((error) => {
        setErrorFooter(JSON.stringify(error));
      });
  };

  return (
    <Modal
      title={`Edit Hostname${mode === "cluster" ? ` for BQN ${bqn} ` : ""}`}
      superIcon="edit"
      content={() => {
        return (
          <div>
            <TextInput
              name="hostname"
              className="input-group"
              onChange={({ target }) => {
                setHostname(target.value);
              }}
              title="Hostname"
              defaultValue={hostname}
              label="Hostname:"
            />
            <ButtonWrapper className="modal-footer">
              <button
                type="button"
                className="btn btn-link waves-effect"
                onClick={() => doApply()}
              >
                APPLY
              </button>
              <button
                type="button"
                className="btn btn-link waves-effect"
                onClick={() => setOpen(false)}
              >
                CANCEL
              </button>
            </ButtonWrapper>
          </div>
        );
      }}
      applyLabel={null}
      onApply={null}
      closeLabel={null}
      onClose={null}
      footerMessage={
        errorFooter ? (
          <span className="modal-err-msg color-red align-left">
            {errorFooter}
          </span>
        ) : null
      }
    />
  );
}

// The BQN selected answers with its hostname alone, while the whole cluster
// answers with the hostname of every BQN: the rows are the same ones either
// way, one BQN of the cluster in each of them. A BQN that answers no name of
// its own, which is what one offline answers, has none to report.
const asRows = (hostnames) =>
  Object.entries(hostnames).map(([bqn, hostname]) => ({
    BQN: bqn,
    HOSTNAME: hostname ?? "n/a",
  }));

// The BQN asked for tells both the command sent and the shape of its answer,
// so it is resolved once and both are taken from it.
const hostnamesRetrieval = (mode, bqnId) => {
  const bqnIdAsked = bqnIdTarget(mode, bqnId);

  return ifCl
    .run(atBqnTargetOrDetail("show system hostname", mode, bqnId))
    .then((response) =>
      bqnIdAsked === undefined
        ? asRows(parseHostnames(response))
        : asRows({ [bqnIdAsked]: parseHostname(response) })
    );
};

function SettingsList() {
  const actions = useContext(ActionsContext);
  const [tableData, setTableData] = useState(null);
  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const isClusterMode = mode === "cluster";

  const doLoad = () => {
    hostnamesRetrieval(mode, bqnId).then((rows) => setTableData(rows));
  };

  // The hostnames are asked again for the BQN selected next.
  useEffect(() => {
    doLoad();
  }, [mode, bqnId]);

  if (!tableData) {
    return null;
  }

  const filteredRows = tableData.filter((row) => {
    if (mode === "standalone" || bqnId === "all") {
      return true;
    }
    return parseFloat(row["BQN"]) === parseFloat(bqnId);
  });

  return (
    <Wrapper>
      <TableContainer isClusterMode={mode === "cluster"}>
        <ArrangeInColumns columns={2} rowGap={0}>
          <Table
            className="list-hosts"
            hasPagination={isClusterMode}
            hasSelectNrRows={isClusterMode}
            isSelectable={false}
            isRowSelectable={false}
            data={filteredRows}
            sort={mode === "cluster"}
            hasColumnFilter={mode === "cluster"}
            columns={[
              ...(mode !== "standalone"
                ? [
                    {
                      label: "BQN",
                      id: "BQN",
                      numeric: true,
                    },
                  ]
                : []),
              {
                label: "HOSTNAME",
                id: "HOSTNAME",
                numeric: false,
              },
              {
                label: "ACTIONS",
                id: "ACTIONS",
                sort: false,
                actions: [
                  {
                    label: "Edit",
                    icon: "edit",
                    onClick: (row) => {
                      actions.send("edit-hostname", {
                        bqn: row["BQN"],
                        hostname: row["HOSTNAME"],
                      });
                    },
                  },
                ],
              },
            ]}
            search={isClusterMode}
            rowsPerPageNr={50}
            exportCSV={false}
          />
        </ArrangeInColumns>
      </TableContainer>
      <HostnameModal onAppliedChange={() => doLoad()} />
    </Wrapper>
  );
}

export default SettingsList;
